import {
	ButtonHTMLAttributes,
	CSSProperties,
	useEffect,
	useRef,
	useState,
} from 'react'
import cn from 'classnames'
import { PlusIcon } from 'assets/icons'
import Loader from 'components/Loader'
import SidebarButton from './SidebarButton'
import { useOutsideClick } from 'hooks'
import { t } from 'helpers'
import s from './index.module.scss'

interface Props {
	open: boolean
	onClose: () => void
	children: JSX.Element
	header?: {
		title: string
		description?: string
	}
	style?: CSSProperties
	containerStyle?: CSSProperties
	iconClose?: boolean
	additional?: boolean
	buttons?: ButtonHTMLAttributes<HTMLButtonElement>[]
	disableBlur?: boolean
	loading?: boolean
}

const Sidebar = ({
	open,
	children,
	onClose,
	style,
	containerStyle,
	header,
	iconClose,
	additional,
	buttons,
	loading = false,
	disableBlur = false,
}: Props): JSX.Element => {
	const [isVisible, setIsVisible] = useState(false)
	const [isRendered, setIsRendered] = useState(false)
	const sidebarRef = useRef(null)

	useOutsideClick(
		sidebarRef,
		iconClose || !open
			? () => null
			: () => {
				const popup = document.getElementById('select-popup')
				if (!popup) onClose()
			},
	)

	useEffect(() => {
		if (open) {
			setIsRendered(true)
			setTimeout(() => setIsVisible(true), 100)
		} else {
			setIsVisible((prev) => {
				if (prev) setTimeout(() => setIsRendered(false), 300)

				return false
			})
		}
	}, [open])

	if (!isRendered) return <></>

	return (
		<>
			{isVisible && !disableBlur ? <div className={s.blur} /> : null}
			<div
				id="sidebar"
				className={cn(s.container, 'hideScrollBar', {
					[s.open]: isVisible && !additional,
					[s.openAdditional]: isVisible && additional,
					[s.additional]: additional,
				})}
				ref={sidebarRef}
				style={containerStyle}>
				<div
					className={s.wrapper}
					style={{
						height: buttons?.length ? '100%' : 'unset',
						paddingBottom: buttons?.length ? '0px' : '34px',
						...style,
					}}>
					<Loader loading={loading} adapt />
					<>
						{header ? (
							<div className={s.header}>
								<div className={s.titleWrapper}>
									<h3 className="h3-b">{t(header?.title)}</h3>
									<div onClick={onClose}>
										<PlusIcon
											width="30px"
											height="30px"
											color="var(--mono400)"
											style={{ transform: 'rotate(45deg)' }}
										/>
									</div>
								</div>
								{header?.description ? (
									<p className="caption-m" style={{ color: 'var(--mono300)' }}>
										{t(header.description)}
									</p>
								) : null}
							</div>
						) : iconClose ? (
							<div className={s.close}>
								<div onClick={onClose}>
									<PlusIcon
										width="30px"
										height="30px"
										color="var(--mono400)"
										style={{ transform: 'rotate(45deg)' }}
									/>
								</div>
							</div>
						) : null}
					</>
					{children}
					{buttons?.length ? (
						<div className={s.buttons}>
							{buttons?.map((buttonProps, index) => (
								<SidebarButton key={index} {...buttonProps} />
							))}
						</div>
					) : null}
				</div>
			</div>
		</>
	)
}

export default Sidebar
